"use client";

import React, { useMemo, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Box, 
  Layers, 
  Code, 
  Cpu,
  Puzzle,
  Wrench,
  Settings,
  Grid3x3,
  Combine,
  Scan,
  Copy,
  ChevronDown,
  Move3d,
  Cuboid,
  Component,
  Workflow
} from "lucide-react";

interface IconItem {
  id: string;
  Icon: React.ElementType;
  color: string;
}

interface IconPosition {
  item: IconItem;
  x: number;
  y: number;
  size: number;
  blur: number;
  rotate: number;
  zIndex: number;
  delay: number;
  animDuration: number;
  animDelay: number;
}

// Blender/3D/Addon-related icons with colors
const ADDONS_ICONS: IconItem[] = [
  { id: "box", Icon: Box, color: "#aaff00" },
  { id: "layers", Icon: Layers, color: "#8cdc1e" },
  { id: "code", Icon: Code, color: "#78c814" },
  { id: "cpu", Icon: Cpu, color: "#aaff00" },
  { id: "puzzle", Icon: Puzzle, color: "#8cdc1e" },
  { id: "wrench", Icon: Wrench, color: "#78c814" },
  { id: "settings", Icon: Settings, color: "#aaff00" },
  { id: "grid", Icon: Grid3x3, color: "#8cdc1e" },
  { id: "combine", Icon: Combine, color: "#78c814" },
  { id: "scan", Icon: Scan, color: "#aaff00" },
  { id: "copy", Icon: Copy, color: "#8cdc1e" },
  { id: "chevrons", Icon: ChevronDown, color: "#78c814" },
  { id: "move3d", Icon: Move3d, color: "#aaff00" },
  { id: "cuboid", Icon: Cuboid, color: "#8cdc1e" },
  { id: "component", Icon: Component, color: "#78c814" },
  { id: "workflow", Icon: Workflow, color: "#aaff00" },
];

// Position templates with relative coordinates (will be scaled)
const POSITION_TEMPLATES = [
  // Center - largest, minimal blur
  { x: 0, y: 0, size: 1.0, blur: 0, zIndex: 50 },
  // Inner ring - large size, very slight blur
  { x: -0.5, y: -0.4, size: 0.6, blur: 0.3, zIndex: 40 },
  { x: 0.55, y: -0.45, size: 0.55, blur: 0.4, zIndex: 35 },
  { x: 0.6, y: 0.65, size: 0.65, blur: 0.2, zIndex: 45 },
  { x: -0.55, y: 0.45, size: 0.5, blur: 0.5, zIndex: 30 },
  // Middle ring - medium size, slight blur
  { x: -0.75, y: 0, size: 0.42, blur: 0.8, zIndex: 25 },
  { x: 0.75, y: 0.1, size: 0.45, blur: 0.6, zIndex: 28 },
  { x: 0, y: -0.8, size: 0.4, blur: 0.9, zIndex: 20 },
  { x: -0.15, y: 0.9, size: 0.42, blur: 0.7, zIndex: 22 },
  // Outer ring - smaller, moderate blur
  { x: -0.9, y: -0.5, size: 0.32, blur: 1.2, zIndex: 15 },
  { x: 0.95, y: 0.6, size: 0.35, blur: 1.0, zIndex: 18 },
  { x: 0.5, y: -0.9, size: 0.3, blur: 1.4, zIndex: 12 },
];

// Fisher-Yates shuffle for true randomization
function shuffleArray<T>(array: T[]): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

interface AddonsIconsClusterProps {
  className?: string;
}

export const AddonsIconsCluster: React.FC<AddonsIconsClusterProps> = ({
  className = "",
}) => {
  const [mounted, setMounted] = useState(false);
  const [shuffledTemplates, setShuffledTemplates] = useState(POSITION_TEMPLATES);
  
  useEffect(() => {
    setMounted(true);
    // Shuffle positions on each load for different arrangements
    setShuffledTemplates(shuffleArray([...POSITION_TEMPLATES]));
  }, []);

  // Map icons to positions
  const iconPositions: IconPosition[] = useMemo(() => {
    const baseScale = 120;
    const spread = 0.85;
    
    return shuffledTemplates.map((template, index) => {
      const item = ADDONS_ICONS[index % ADDONS_ICONS.length];
      // Random rotation between -20 and 20 degrees
      const randomRotate = -20 + Math.random() * 40;
      
      return {
        item,
        x: template.x * baseScale * spread,
        y: template.y * baseScale * spread,
        size: Math.round(template.size * baseScale),
        blur: template.blur,
        rotate: randomRotate,
        zIndex: template.zIndex,
        delay: index * 0.05,
        animDuration: 4 + (index % 4) * 0.8,
        animDelay: index * 0.12,
      };
    });
  }, [shuffledTemplates]);

  // Prevent hydration mismatch by rendering a placeholder during SSR
  if (!mounted) {
    return (
      <div
        className={`relative w-full h-full min-h-[300px] flex items-center justify-center decorative-icon-cluster ${className}`}
        style={{ perspective: "1000px" }}
      >
        <div className="absolute" style={{ left: "50%", top: "50%", transform: "translate(-50%, -50%)" }}>
          <div className="w-[120px] h-[120px] rounded-full border border-white/20" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative w-full h-full min-h-[300px] flex items-center justify-center decorative-icon-cluster ${className}`}
      style={{ perspective: "1000px" }}
    >
      {iconPositions.map((pos, index) => {
        const IconComponent = pos.item.Icon;
        return (
          <motion.div
            key={`${pos.item.id}-${index}`}
            initial={{ opacity: 0, scale: 0.5, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{
              duration: 0.6,
              delay: pos.delay,
              ease: [0.25, 0.1, 0.25, 1],
            }}
            className="absolute"
            style={{
              left: `calc(50% + ${pos.x * 1.5}px)`,
              top: `calc(50% + ${pos.y * 1.5}px)`,
              transform: `translate(-50%, -50%) rotate(${pos.rotate}deg)`,
              zIndex: pos.zIndex,
            }}
          >
            <motion.div
              animate={{
                y: [0, -10, 0],
                rotate: [pos.rotate, pos.rotate + 3, pos.rotate - 3, pos.rotate],
              }}
              transition={{
                y: {
                  duration: pos.animDuration,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: pos.animDelay,
                },
                rotate: {
                  duration: pos.animDuration * 1.3,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: pos.animDelay * 1.1,
                },
              }}
              className="relative"
            >
              <IconComponent
                size={pos.size}
                color={pos.item.color}
                strokeWidth={1.5}
                style={{
                  filter: `blur(${pos.blur}px)`,
                  opacity: pos.blur > 0.8 ? 0.55 : 0.85,
                }}
              />
            </motion.div>
          </motion.div>
        );
      })}
    </div>
  );
};
